import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { AuthContext } from '../../../components/AuthContext';
import { FaSearch, FaFilter, FaDownload, FaEye, FaTasks, FaTimes, FaClock, FaChevronLeft, FaChevronRight, FaPlus } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const ActivityLogsReports = () => {
  const { user } = useContext(AuthContext);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logType, setLogType] = useState('activitynew');  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterItem, setFilterItem] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(15);
  const [employees, setEmployees] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [additionalRemarks, setAdditionalRemarks] = useState('');
  const [extraRemark, setExtraRemark] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showExtraRemarkModal, setShowExtraRemarkModal] = useState(false);
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    cancelled: 0,
    draft: 0,
    service: 0,
    quotationIssued: 0
  });
  const [pieChartData, setPieChartData] = useState({
    labels: [],
    datasets: []
  });
  const [lineChartData, setLineChartData] = useState({
    labels: [],
    datasets: []
  });

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const statusOptions = ['Pending', 'Completed', 'Cancelled', 'Draft', 'Service', 'Quotation issued'];

  const prepareLineChartData = () => {
    // Get last 7 days
    const last7Days = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      last7Days.push({
        date: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      });
    }

    // Count logs created per day from all activity logs (not just current page)
    const logCounts = last7Days.map(day => {
      const count = activityLogs.filter(log => {
        const logDate = new Date(log.createdAt).toISOString().split('T')[0];
        return logDate === day.date;
      }).length;
      return count;
    });

    setLineChartData({
      labels: last7Days.map(day => day.label),
      datasets: [
        {
          label: 'Activity Logs Created',
          data: logCounts,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.1,
          fill: true
        }
      ]
    });
  };

  const prepareLineChartDataFromAPI = (lineData) => {
    setLineChartData({
      labels: lineData.labels,
      datasets: [
        {
          label: 'Activity Logs Created',
          data: lineData.data,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.1,
          fill: true
        }
      ]
    });
  };

  useEffect(() => {
    fetchActivityLogs();
    fetchEmployees();
  }, [currentPage, logType, filterDateFrom, filterDateTo, filterStatus, filterEmployee, filterCategory, filterItem, searchTerm]);

  const fetchActivityLogs = async () => {
    setLoading(true);
    try {
      const params = {
        companyHeadId: user.user._id,
        logType,
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        dateFrom: filterDateFrom,
        dateTo: filterDateTo,
        status: filterStatus,
        employeeId: filterEmployee,
        category: filterCategory,
        item: filterItem
      };

      // Remove empty parameters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await axios.get(`${API_BASE}/reports/activitylogs`, { params });
      
      if (response.data.success) {
        setActivityLogs(response.data.data);
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalCount(response.data.pagination.totalCount || 0);
        
        // Set statistics and pie chart data from response
        if (response.data.statistics) {
          setStatistics(response.data.statistics);
        }
        if (response.data.pieChartData) {
          preparePieChartData(response.data.pieChartData);
        }
        if (response.data.lineChartData) {
          prepareLineChartDataFromAPI(response.data.lineChartData);
        }
      } else {
        toast.error('Failed to fetch activity logs');
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const preparePieChartData = (pieData) => {
    // Add Quotation issued to pie chart if present
    const statusMap = {
      Pending: '#fbbf24',
      Completed: '#10b981',
      Cancelled: '#ef4444',
      Draft: '#6b7280',
      Service: '#8b5cf6',
      'Quotation issued': '#3b82f6'
    };
    const labels = Object.keys(statusMap);
    const data = labels.map(label => pieData[label.toLowerCase()] || 0);
    const backgroundColors = labels.map(label => statusMap[label]);
    setPieChartData({
      labels,
      datasets: [{
        data,
        backgroundColor: backgroundColors
      }]
    });
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_BASE}/reports/activitylogs/employees/list?companyHeadId=${user.user._id}`);
      if (response.data.success) {
        setEmployees(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleStatusUpdate = (log) => {
    // Allow viewing details for all logs, but only allow status updates for pending logs
    // For pending logs, check if status has already been updated
    if (log.status === 'Pending' && log.isStatusUpdated) {
      toast.warning('Status has already been updated and cannot be changed again');
      return;
    }

    setSelectedLog(log);
    // Set initial new status to first non-pending option for pending logs
    if (log.status === 'Pending') {
      setNewStatus('Completed'); // Default to Completed for pending logs
    } else {
      setNewStatus(log.status);
    }
    setAdditionalRemarks('');
    setShowStatusModal(true);
  };

  const handleExtraRemark = (log) => {
    // Only allow adding extra remarks to pending logs
    if (log.status !== 'Pending' && log.status !== 'Quotation issued') {
      toast.warning('Extra remarks can only be added to pending logs');
      return;
    }

    setSelectedLog(log);
    setExtraRemark('');
    setShowExtraRemarkModal(true);
  };

  const handleExtraRemarkSubmit = async () => {
    if (!extraRemark.trim()) {
      toast.error('Please enter an extra remark');
      return;
    }

    try {
      const response = await axios.put(`${API_BASE}/reports/activitylogs/${selectedLog._id}/extraremarks`, {
        extraRemark,
        logType,
        companyHeadId: user.user._id
      });

      if (response.data.success) {
        toast.success('Extra remark added successfully');
        setShowExtraRemarkModal(false);
        fetchActivityLogs();
      } else {
        toast.error(response.data.message || 'Failed to add extra remark');
      }
    } catch (error) {
      console.error('Error adding extra remark:', error);
      toast.error(error.response?.data?.message || 'Failed to add extra remark');
    }
  };

  const handleStatusSubmit = async () => {
    // Only allow status updates for pending logs
    if (selectedLog.status !== 'Pending') {
      toast.error('Status can only be updated for pending logs');
      return;
    }

    // Validate that new status is not Pending
    if (newStatus === 'Pending') {
      toast.error('Status cannot be changed back to Pending');
      return;
    }

    // Validate that status is actually changing
    if (newStatus === selectedLog.status && !additionalRemarks.trim()) {
      toast.warning('Please change the status or add additional remarks');
      return;
    }

    try {
      const response = await axios.put(`${API_BASE}/reports/activitylogs/${selectedLog._id}/status`, {
        status: newStatus,
        additionalRemarks,
        logType,
        companyHeadId: user.user._id
      });

      if (response.data.success) {
        toast.success('Status updated successfully');
        setShowStatusModal(false);
        fetchActivityLogs();
      } else {
        toast.error(response.data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        companyHeadId: user.user._id,
        logType,
        search: searchTerm,
        dateFrom: filterDateFrom,
        dateTo: filterDateTo,
        status: filterStatus,
        employeeId: filterEmployee,
        category: filterCategory,
        item: filterItem
      };

      const response = await axios.get(`${API_BASE}/reports/activitylogs/export/csv`, { 
        params,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity-logs-${logType}-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Activity logs exported successfully');
    } catch (error) {
      console.error('Error exporting activity logs:', error);
      toast.error('Failed to export activity logs');
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString()
    };
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800',
      'Draft': 'bg-gray-100 text-gray-800',
      'Service': 'bg-purple-100 text-purple-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryColor = (category) => {
    const categoryColors = {
      'Reference': 'bg-teal-100 text-teal-800',
      'Guest': 'bg-cyan-100 text-cyan-800'
    };
    return categoryColors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <FaClock className="mr-3 text-blue-600" />
          Activity Logs Reports
        </h2>
        <button
          onClick={handleExport}
          className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-green-700 transition-colors"
        >
          <FaDownload className="mr-2" />
          Export CSV
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-800">Total Logs</h3>
          <p className="text-2xl font-bold text-blue-600">{statistics.total || totalCount}</p>
          <p className="text-sm text-blue-600 mt-1">All matching records</p>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-yellow-800">Pending</h3>
          <p className="text-2xl font-bold text-yellow-600">{statistics.pending || 0}</p>
          <p className="text-sm text-yellow-600 mt-1">
            Current page: {activityLogs.filter(log => log.status === 'Pending').length}
          </p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-green-800">Completed</h3>
          <p className="text-2xl font-bold text-green-600">{statistics.completed || 0}</p>
          <p className="text-sm text-green-600 mt-1">
            Current page: {activityLogs.filter(log => log.status === 'Completed').length}
          </p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-red-800">Cancelled</h3>
          <p className="text-2xl font-bold text-red-600">{statistics.cancelled || 0}</p>
          <p className="text-sm text-red-600 mt-1">
            Current page: {activityLogs.filter(log => log.status === 'Cancelled').length}
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-800">Draft</h3>
          <p className="text-2xl font-bold text-gray-600">{statistics.draft || 0}</p>
          <p className="text-sm text-gray-600 mt-1">
            Current page: {activityLogs.filter(log => log.status === 'Draft').length}
          </p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-purple-800">Service</h3>
          <p className="text-2xl font-bold text-purple-600">{statistics.service || 0}</p>
          <p className="text-sm text-purple-600 mt-1">
            Current page: {activityLogs.filter(log => log.status === 'Service').length}
          </p>
        </div>
        <div className="bg-indigo-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-indigo-800">Quotation Issued</h3>
          <p className="text-2xl font-bold text-indigo-600">{statistics.quotationIssued || 0}</p>
          <p className="text-sm text-indigo-600 mt-1">
            Current page: {activityLogs.filter(log => log.status === 'Quotation issued').length}
          </p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Line Chart */}
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Activity Logs Created - Last 7 Days</h3>
          <div className="h-64">
            <Line 
              data={lineChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: false,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1,
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Pie Chart */}
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Status Distribution</h3>
          <div className="h-64">
            <Pie 
              data={pieChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      boxWidth: 12,
                      padding: 15,
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const label = context.label || '';
                        const value = context.raw;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${label}: ${value} (${percentage}%)`;
                      }
                    }
                  }
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Log Type Selection and Filters */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Report Type:</label>
            <select
              value={logType}
              onChange={(e) => {
                setLogType(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="activity">Activity Logs</option>
              <option value="activitynew">Activity Logs New</option>
            </select>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <input
            type="date"
            placeholder="From Date"
            value={filterDateFrom}
            onChange={(e) => {
              setFilterDateFrom(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          
          <input
            type="date"
            placeholder="To Date"
            value={filterDateTo}
            onChange={(e) => {
              setFilterDateTo(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          
          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            {statusOptions.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          
          <select
            value={filterEmployee}
            onChange={(e) => {
              setFilterEmployee(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Employees</option>
            {employees.map(employee => (
              <option key={employee._id} value={employee._id}>{employee.name}</option>
            ))}
          </select>

          {logType === 'activitynew' && (
            <>
              <select
                value={filterCategory}
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                <option value="Retail">Retail</option>
                <option value="Builder">Builder</option>
                <option value="Architect">Architect</option>
                <option value="Contractor">Contractor</option>
                <option value="Interior">Interior</option>
              </select>
            </>
          )}

          {logType === 'activitynew' && (
            <select
              value={filterItem}
              onChange={(e) => {
                setFilterItem(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Items</option>
              <option value="Tiles">Tiles</option>
              <option value="Sanitary Ware">Sanitary Ware</option>
              <option value="Kithen">Kitchen</option>
              <option value="Doors">Doors</option>
              <option value="Windows">Windows</option>
              <option value="Others">Others</option>
            </select>
          )}
        </div>
      </div>

      {/* Activity Logs Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              {logType === 'activity' ? (
                <>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact Person
                  </th>
                </>
              ) : (
                <>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Guest/Reference
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                </>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created By
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2">Loading activity logs...</span>
                  </div>
                </td>
              </tr>
            ) : activityLogs.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  No activity logs found
                </td>
              </tr>
            ) : (
              activityLogs.map((log, index) => {
                const { date } = formatDateTime(log.date);
                return (
                  <tr key={log._id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                      {log.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {date}
                    </td>
                    {logType === 'activity' ? (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.project}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.contactPerson}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.guest?.guestName || 
                           log.reference?.name ||
                           'No Guest/Reference'}
                          {log.guest && (
                            <div className="text-xs text-gray-500">Guest: {log.guest.phoneNo}</div>
                          )}
                          {log.reference && (
                            <div className="text-xs text-gray-500">Ref: {log.reference.phoneNumber}</div>
                          )}
                          {log.guest?.station?.name && (
                            <div className="text-xs text-gray-500">Station: {log.guest.station.name}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(log.category)}`}>
                            {log.category}
                          </span>
                        </td>
                      </>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(log.status)}`}>
                        {log.status}
                      </span>
                      {log.isStatusUpdated && (
                        <span className="ml-2 text-xs text-gray-500">(Updated)</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.createdBy?.name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleStatusUpdate(log)}
                          className="text-blue-600 hover:text-blue-800"
                          title={
                            log.status === 'Completed'
                              ? 'View Details'
                              : log.status === 'Pending' && !log.isStatusUpdated
                              ? 'Update Status'
                              : log.status === 'Pending' && log.isStatusUpdated
                              ? 'View Details (Status already updated)'
                              : 'View Details'
                          }
                        >
                          <FaTasks />
                        </button>
                        
                        {(log.status === 'Pending' || (log.status === 'Quotation issued' && log.isStatusUpdated)) && (
                          <button
                            onClick={() => handleExtraRemark(log)}
                            className="text-green-600 hover:text-green-800"
                            title="Add Extra Remark"
                          >
                            <FaPlus />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-700">
            Showing page {currentPage} of {totalPages} ({totalCount} total records)
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <FaChevronLeft className="mr-1" />
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNumber = Math.max(1, currentPage - 2) + i;
              if (pageNumber > totalPages) return null;
              
              return (
                <button
                  key={pageNumber}
                  onClick={() => setCurrentPage(pageNumber)}
                  className={`px-3 py-1 border rounded-md text-sm font-medium ${
                    currentPage === pageNumber
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              Next
              <FaChevronRight className="ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div>
                <h3 className="text-2xl font-bold text-gray-800">
                  {selectedLog.status === 'Pending' ? 'Update Activity Log Status' : 'Activity Log Details'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Log ID: <span className="font-semibold text-blue-600">{selectedLog.id}</span> - 
                  <span className={`font-semibold ml-1 ${
                    selectedLog.status === 'Completed' ? 'text-green-600' : 
                    selectedLog.status === 'Pending' ? 'text-yellow-600' : 'text-gray-600'
                  }`}>
                    {selectedLog.status}
                  </span>
                </p>
              </div>
              <button
                onClick={() => setShowStatusModal(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition-colors"
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Log Details Section */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 mb-6 border border-gray-200">
                <div className="flex items-center mb-4">
                  <FaEye className="text-blue-600 mr-2" />
                  <h4 className="text-xl font-bold text-gray-800">Activity Log Details</h4>
                </div>
                
                {logType === 'activity' ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <h5 className="font-semibold text-gray-700 mb-3 text-lg border-b border-gray-200 pb-2">Project Information</h5>
                        <div className="space-y-2">
                          <div className="flex">
                            <span className="font-medium text-gray-600 w-24">Project:</span>
                            <span className="text-gray-800 font-semibold">{selectedLog.project}</span>
                          </div>
                          <div className="flex">
                            <span className="font-medium text-gray-600 w-24">Date:</span>
                            <span className="text-gray-800">{formatDateTime(selectedLog.date).date}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <h5 className="font-semibold text-gray-700 mb-3 text-lg border-b border-gray-200 pb-2">Contact Information</h5>
                        <div className="space-y-2">
                          <div className="flex">
                            <span className="font-medium text-gray-600 w-32">Contact Person:</span>
                            <span className="text-gray-800">{selectedLog.contactPerson}</span>
                          </div>
                          <div className="flex">
                            <span className="font-medium text-gray-600 w-32">Contact Number:</span>
                            <span className="text-gray-800 font-mono">{selectedLog.contactNumber}</span>
                          </div>
                          <div className="flex">
                            <span className="font-medium text-gray-600 w-32">Station:</span>
                            <span className="text-gray-800">{selectedLog.station?.name || 'N/A'}</span>
                          </div>
                          <div className="flex">
                            <span className="font-medium text-gray-600 w-32">Reference:</span>
                            <span className="text-gray-800">{selectedLog.referenceName}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Right Column */}
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <h5 className="font-semibold text-gray-700 mb-3 text-lg border-b border-gray-200 pb-2">Requirements</h5>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-gray-800 leading-relaxed">
                            {Array.isArray(selectedLog.requirements) ? selectedLog.requirements.join(', ') : selectedLog.requirements}
                          </p>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <h5 className="font-semibold text-gray-700 mb-3 text-lg border-b border-gray-200 pb-2">Additional Information</h5>
                        <div className="space-y-2">
                          <div className="flex">
                            <span className="font-medium text-gray-600 w-24">Created By:</span>
                            <span className="text-gray-800">{selectedLog.createdBy?.name || 'Unknown'}</span>
                          </div>
                          {selectedLog.statusUpdatedBy && (
                            <div className="flex">
                              <span className="font-medium text-gray-600 w-24">Updated By:</span>
                              <span className="text-gray-800">{selectedLog.statusUpdatedBy.username || selectedLog.statusUpdatedBy.name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Full Width Remarks */}
                    <div className="lg:col-span-2">
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <h5 className="font-semibold text-gray-700 mb-3 text-lg border-b border-gray-200 pb-2">Remarks</h5>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-gray-800 leading-relaxed">{selectedLog.remarks}</p>
                        </div>
                        {selectedLog.additionalRemarks && (
                          <>
                            <h5 className="font-semibold text-gray-700 mb-2 mt-4">Additional Remarks</h5>
                            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                              <p className="text-gray-800 leading-relaxed">{selectedLog.additionalRemarks}</p>
                            </div>
                          </>
                        )}
                        {selectedLog.extraRemarks && selectedLog.extraRemarks.length > 0 && (
                          <>
                            <h5 className="font-semibold text-gray-700 mb-2 mt-4">Extra Remarks</h5>
                            <div className="space-y-2">
                              {selectedLog.extraRemarks.map((remark, index) => (
                                <div key={index} className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                                  <p className="text-gray-800 leading-relaxed">{remark.text}</p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Added on: {new Date(remark.date).toLocaleString()}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <h5 className="font-semibold text-gray-700 mb-3 text-lg border-b border-gray-200 pb-2">Guest/Reference Information</h5>
                        <div className="space-y-2">
                          <div className="flex">
                            <span className="font-medium text-gray-600 w-28">Name:</span>
                            <span className="text-gray-800">
                              {selectedLog.guest?.guestName || 
                               selectedLog.reference?.name ||
                               'No Guest/Reference'}
                            </span>
                          </div>
                          {selectedLog.guest && (
                            <>
                              <div className="flex">
                                <span className="font-medium text-gray-600 w-28">Phone:</span>
                                <span className="text-gray-800 font-mono">{selectedLog.guest.phoneNo}</span>
                              </div>
                              <div className="flex">
                                <span className="font-medium text-gray-600 w-28">Station:</span>
                                <span className="text-gray-800">{selectedLog.guest.station?.name || selectedLog.guest.station}</span>
                              </div>
                              <div className="flex">
                                <span className="font-medium text-gray-600 w-28">Reference:</span>
                                <span className="text-gray-800">{selectedLog.guest.reference}</span>
                              </div>
                            </>
                          )}
                          {selectedLog.reference && (
                            <>
                              <div className="flex">
                                <span className="font-medium text-gray-600 w-28">Phone:</span>
                                <span className="text-gray-800 font-mono">{selectedLog.reference.phoneNumber}</span>
                              </div>
                              <div className="flex">
                                <span className="font-medium text-gray-600 w-28">Type:</span>
                                <span className="text-gray-800">{selectedLog.reference.type}</span>
                              </div>
                              <div className="flex">
                                <span className="font-medium text-gray-600 w-28">Class:</span>
                                <span className="text-gray-800">{selectedLog.reference.classification}</span>
                              </div>
                            </>
                          )}
                          <div className="flex">
                            <span className="font-medium text-gray-600 w-28">Category:</span>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(selectedLog.category)}`}>
                              {selectedLog.category}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <h5 className="font-semibold text-gray-700 mb-3 text-lg border-b border-gray-200 pb-2">Additional Information</h5>
                        <div className="space-y-2">
                          <div className="flex">
                            <span className="font-medium text-gray-600 w-24">Created By:</span>
                            <span className="text-gray-800">{selectedLog.createdBy?.name || 'Unknown'}</span>
                          </div>
                          <div className="flex">
                            <span className="font-medium text-gray-600 w-24">Date:</span>
                            <span className="text-gray-800">{formatDateTime(selectedLog.date).date}</span>
                          </div>
                          {selectedLog.statusUpdatedBy && (
                            <div className="flex">
                              <span className="font-medium text-gray-600 w-24">Updated By:</span>
                              <span className="text-gray-800">{selectedLog.statusUpdatedBy.username}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Right Column */}
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <h5 className="font-semibold text-gray-700 mb-3 text-lg border-b border-gray-200 pb-2">Items</h5>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-gray-800 leading-relaxed">
                            {Array.isArray(selectedLog.items) ? selectedLog.items.join(', ') : selectedLog.items}
                          </p>
                        </div>
                      </div>
                      
                      {(selectedLog.interior || selectedLog.architect) && (
                        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                          <h5 className="font-semibold text-gray-700 mb-3 text-lg border-b border-gray-200 pb-2">Professional Contacts</h5>
                          <div className="space-y-2">
                            {selectedLog.interior && (
                              <div className="flex">
                                <span className="font-medium text-gray-600 w-20">Interior:</span>
                                <span className="text-gray-800">{selectedLog.interior.name} - {selectedLog.interior.phoneNumber}</span>
                              </div>
                            )}
                            {selectedLog.architect && (
                              <div className="flex">
                                <span className="font-medium text-gray-600 w-20">Architect:</span>
                                <span className="text-gray-800">{selectedLog.architect.name} - {selectedLog.architect.phoneNumber}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Full Width Remarks */}
                    <div className="lg:col-span-2">
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <h5 className="font-semibold text-gray-700 mb-3 text-lg border-b border-gray-200 pb-2">Remarks</h5>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-gray-800 leading-relaxed">{selectedLog.remarks}</p>
                        </div>
                        {selectedLog.additionalRemarks && (
                          <>
                            <h5 className="font-semibold text-gray-700 mb-2 mt-4">Additional Remarks</h5>
                            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                              <p className="text-gray-800 leading-relaxed">{selectedLog.additionalRemarks}</p>
                            </div>
                          </>
                        )}
                        {selectedLog.extraRemarks && selectedLog.extraRemarks.length > 0 && (
                          <>
                            <h5 className="font-semibold text-gray-700 mb-2 mt-4">Extra Remarks</h5>
                            <div className="space-y-2">
                              {selectedLog.extraRemarks.map((remark, index) => (
                                <div key={index} className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                                  <p className="text-gray-800 leading-relaxed">{remark.text}</p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Added on: {new Date(remark.date).toLocaleString()}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Conditional Status Update Section for Pending Logs */}
              {selectedLog.status === 'Pending' && !selectedLog.isStatusUpdated && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center mb-4">
                    <FaTasks className="text-blue-600 mr-2" />
                    <h4 className="text-xl font-bold text-gray-800">Update Status</h4>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Current Status</label>
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedLog.status)}`}>
                          {selectedLog.status}
                        </span>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">New Status *</label>
                      <select
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        required
                      >
                        {statusOptions.filter(status => status !== 'Pending').map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Note: Status cannot be changed back to Pending</p>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Remarks</label>
                    <textarea
                      value={additionalRemarks}
                      onChange={(e) => setAdditionalRemarks(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      rows="4"
                      placeholder="Add any additional remarks about this status update..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">Optional: Provide additional context for the status change</p>
                  </div>

                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center">
                      <div className="text-yellow-600 mr-2">⚠️</div>
                      <div>
                        <p className="text-sm font-medium text-yellow-800">Important:</p>
                        <p className="text-sm text-yellow-700">Once you update the status, it cannot be changed again. Please ensure the status change is correct.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Status Information for Already Updated Pending Logs */}
              {selectedLog.status === 'Pending' && selectedLog.isStatusUpdated && (
                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200">
                  <div className="flex items-center mb-4">
                    <FaTasks className="text-yellow-600 mr-2" />
                    <h4 className="text-xl font-bold text-gray-800">Status Information</h4>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-yellow-200">
                    <p className="text-amber-800 font-medium">
                      This pending log has already been updated and cannot be modified again.
                    </p>
                    {selectedLog.statusUpdatedAt && (
                      <p className="text-sm text-gray-600 mt-2">
                        Updated on: {new Date(selectedLog.statusUpdatedAt).toLocaleString()}
                      </p>
                    )}
                    {selectedLog.statusUpdatedBy && (
                      <p className="text-sm text-gray-600">
                        Updated by: {selectedLog.statusUpdatedBy.username || selectedLog.statusUpdatedBy.name}
                      </p>
                    )}
                  </div>

                  {selectedLog.additionalRemarks && (
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Update Remarks</label>
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="text-gray-800">{selectedLog.additionalRemarks}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Status Information Section - Read Only for Completed Logs */}
              {selectedLog.status === 'Completed' && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                <div className="flex items-center mb-4">
                  <FaTasks className="text-green-600 mr-2" />
                  <h4 className="text-xl font-bold text-gray-800">Status Information</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Current Status</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedLog.status)}`}>
                        {selectedLog.status}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status Updated</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <span className="text-gray-800">
                        {selectedLog.statusUpdatedAt 
                          ? new Date(selectedLog.statusUpdatedAt).toLocaleString()
                          : 'Not Available'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {selectedLog.additionalRemarks && (
                  <div className="mt-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status Update Remarks</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-gray-800">{selectedLog.additionalRemarks}</p>
                    </div>
                  </div>
                )}

                {selectedLog.statusUpdatedBy && (
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status Updated By</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-gray-800">{selectedLog.statusUpdatedBy.username || selectedLog.statusUpdatedBy.name}</p>
                    </div>
                  </div>
                )}
                </div>
              )}

              {/* Status Information Section - Read Only for Cancelled Logs */}
              {selectedLog.status === 'Cancelled' && (
                <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-6 border border-red-200">
                <div className="flex items-center mb-4">
                  <FaTasks className="text-red-600 mr-2" />
                  <h4 className="text-xl font-bold text-gray-800">Status Information</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Current Status</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedLog.status)}`}>
                        {selectedLog.status}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status Updated</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <span className="text-gray-800">
                        {selectedLog.statusUpdatedAt 
                          ? new Date(selectedLog.statusUpdatedAt).toLocaleString()
                          : 'Not Available'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {selectedLog.additionalRemarks && (
                  <div className="mt-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status Update Remarks</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-gray-800">{selectedLog.additionalRemarks}</p>
                    </div>
                  </div>
                )}

                {selectedLog.statusUpdatedBy && (
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status Updated By</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-gray-800">{selectedLog.statusUpdatedBy.username || selectedLog.statusUpdatedBy.name}</p>
                    </div>
                  </div>
                )}
                </div>
              )}

              {/* Status Information Section - Read Only for Draft Logs */}
              {selectedLog.status === 'Draft' && (
                <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center mb-4">
                  <FaTasks className="text-gray-600 mr-2" />
                  <h4 className="text-xl font-bold text-gray-800">Status Information</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Current Status</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedLog.status)}`}>
                        {selectedLog.status}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status Updated</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <span className="text-gray-800">
                        {selectedLog.statusUpdatedAt 
                          ? new Date(selectedLog.statusUpdatedAt).toLocaleString()
                          : 'Not Available'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {selectedLog.additionalRemarks && (
                  <div className="mt-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status Update Remarks</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-gray-800">{selectedLog.additionalRemarks}</p>
                    </div>
                  </div>
                )}

                {selectedLog.statusUpdatedBy && (
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status Updated By</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-gray-800">{selectedLog.statusUpdatedBy.username || selectedLog.statusUpdatedBy.name}</p>
                    </div>
                  </div>
                )}
                </div>
              )}

              {/* Status Information Section - Read Only for Service Logs */}
              {selectedLog.status === 'Service' && (
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                <div className="flex items-center mb-4">
                  <FaTasks className="text-purple-600 mr-2" />
                  <h4 className="text-xl font-bold text-gray-800">Status Information</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Current Status</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedLog.status)}`}>
                        {selectedLog.status}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status Updated</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <span className="text-gray-800">
                        {selectedLog.statusUpdatedAt 
                          ? new Date(selectedLog.statusUpdatedAt).toLocaleString()
                          : 'Not Available'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {selectedLog.additionalRemarks && (
                  <div className="mt-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status Update Remarks</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-gray-800">{selectedLog.additionalRemarks}</p>
                    </div>
                  </div>
                )}

                {selectedLog.statusUpdatedBy && (
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status Updated By</label>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <p className="text-gray-800">{selectedLog.statusUpdatedBy.username || selectedLog.statusUpdatedBy.name}</p>
                    </div>
                  </div>
                )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors font-medium"
              >
                {selectedLog.status === 'Pending' && !selectedLog.isStatusUpdated ? 'Cancel' : 'Close'}
              </button>
              {selectedLog.status === 'Pending' && !selectedLog.isStatusUpdated && (
                <button
                  onClick={handleStatusSubmit}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
                >
                  Update Status
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Extra Remark Modal */}
      {showExtraRemarkModal && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-yellow-50 to-orange-50">
              <div>
                <h3 className="text-2xl font-bold text-gray-800">Add Extra Remark</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Log ID: <span className="font-semibold text-blue-600">{selectedLog.id}</span> - 
                  <span className="font-semibold ml-1 text-yellow-600">Pending</span>
                </p>
              </div>
              <button
                onClick={() => setShowExtraRemarkModal(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition-colors"
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200 mb-6">
                <div className="flex items-center mb-4">
                  <FaPlus className="text-orange-600 mr-2" />
                  <h4 className="text-xl font-bold text-gray-800">Add Extra Remark</h4>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-orange-200 mb-4">
                  <p className="text-orange-800 font-medium mb-2">
                    You are adding an extra remark to this pending activity log.
                  </p>
                  <p className="text-sm text-gray-600">
                    Extra remarks can be used to add additional notes or updates before changing the status.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Extra Remark *</label>
                  <textarea
                    value={extraRemark}
                    onChange={(e) => setExtraRemark(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                    rows="4"
                    placeholder="Enter your extra remark here..."
                    required
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">This remark will be timestamped and added to the log.</p>
                </div>

                {/* Show existing extra remarks if any */}
                {selectedLog.extraRemarks && selectedLog.extraRemarks.length > 0 && (
                  <div className="mt-6">
                    <h5 className="font-semibold text-gray-700 mb-2">Existing Extra Remarks</h5>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {selectedLog.extraRemarks.map((remark, index) => (
                        <div key={index} className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-gray-800 leading-relaxed text-sm">{remark.text}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Added on: {new Date(remark.date).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowExtraRemarkModal(false)}
                className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleExtraRemarkSubmit}
                className="px-6 py-3 bg-gradient-to-r from-orange-600 to-yellow-600 text-white rounded-lg hover:from-orange-700 hover:to-yellow-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
              >
                Add Extra Remark
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLogsReports;